import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Route, BrowserRouter as Router, Routes } from "react-router-dom";
import ContactUs from "./components/ContactUs";
import Footer from "./components/Footer";
import Header from "./components/Header";
import OptimizedImage from "./components/OptimizedImage";
import ResponsiveHeadshot from "./components/ResponsiveHeadshot";
import ResponsiveHeroBackground from "./components/ResponsiveHeroBackground";
import ScrollToTop from "./components/ScrollToTop";
import AboutPage from "./pages/About";
import Services from "./pages/CoachingServices";
import CommunityResourcesPage from "./pages/CommunityResources";
import PrivacyPolicy from "./pages/Policy";
import TermsPage from "./pages/TermsPage";
import WebsiteDisclaimer from "./pages/WebsiteDisclaimer";
import WorkshopsAndSeminarsPage from "./pages/WorkshopsAndSeminars";
import "./Style.css";

function Home() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const toggleContactModal = () => {
    setIsContactModalOpen(!isContactModalOpen);
  };

  return (
    <>
      <Helmet>
        <title>
          Certified Personal Financial Coach Holly Springs NC | Virtual
          Financial Coaching | Own Your Money
        </title>
        <meta
          name="description"
          content="Certified personal financial wellness consultant in Holly Springs NC offering virtual financial coaching nationwide. Expert debt management, budget planning help for couples, and financial accountability coaching. Free consultation available."
        />
        <meta
          name="keywords"
          content="certified personal financial coach, 1 on 1 financial coaching, virtual financial coaching sessions, financial wellness consultant, debt management coaching, budget planning help couples, financial accountability partner, Holly Springs NC financial coach, certified financial wellness consultant NC"
        />
        <link rel="canonical" href="https://www.ownyourmoney.biz" />
        <meta
          property="og:title"
          content="Own Your Money | Certified Financial Coaching Services"
        />
        <meta
          property="og:description"
          content="Professional financial coaching services to help individuals and couples nationwide build confidence with their money and create lasting financial peace."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ownyourmoney.biz" />
        <meta
          property="og:image"
          content="https://www.ownyourmoney.biz/og-image.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="geo.region" content="US-NC" />
        <meta name="geo.country" content="United States" />
        <meta name="geo.placename" content="Holly Springs, NC" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en-US" />

        {/* Main Entity Schema */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "@id": "https://www.ownyourmoney.biz#organization",
      "name": "Own Your Money LLC",
      "url": "https://www.ownyourmoney.biz",
      "logo": "https://www.ownyourmoney.biz/static/media/logo.a289caf420b85a4e241ff32552050a01.svg",
      "image": "https://www.ownyourmoney.biz/og-image.png?v=2",
      "description": "Personalized financial coaching and consulting helping clients across North Carolina and the U.S. gain clarity, confidence, and control over their money.",
      "founder": {
        "@type": "Person",
        "@id": "https://www.ownyourmoney.biz/about#wendytoh",
        "name": "Wendy Toh",
        "jobTitle": "Certified Personal Financial Wellness Consultant",
        "sameAs": [
          "https://www.linkedin.com/in/wendytoh"
        ]
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+1-919-285-1689",
          "contactType": "customer service",
          "areaServed": ["US", "NC"],
          "availableLanguage": ["English"],
          "email": "OwnYourMoneyNC@gmail.com"
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Holly Springs",
        "addressRegion": "NC",
        "addressCountry": "US"
      },
      "areaServed": [
        { "@type": "State", "name": "North Carolina" },
        { "@type": "Country", "name": "United States" }
      ],
      "serviceType": [
        "Financial Coaching",
        "Budget Planning",
        "Financial Wellness",
        "Debt Management",
        "Couples Financial Coaching"
      ],
      "hasPart": [
        {
          "@type": "WebPage",
          "@id": "https://www.ownyourmoney.biz/about#about",
          "url": "https://www.ownyourmoney.biz/about",
          "name": "About Wendy Toh"
        },
        {
          "@type": "WebPage",
          "@id": "https://www.ownyourmoney.biz/coachingservices#services",
          "url": "https://www.ownyourmoney.biz/coachingservices",
          "name": "Financial Coaching Services"
        },
        {
          "@type": "WebPage",
          "@id": "https://www.ownyourmoney.biz/communityresources#resources",
          "url": "https://www.ownyourmoney.biz/communityresources",
          "name": "Community Resources"
        }
      ],
      "sameAs": [
        "https://www.linkedin.com/in/wendytoh",
        "https://www.facebook.com/ownyourmoney"
      ],
      "priceRange": "$$"
    }
    `}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://www.ownyourmoney.biz/"
        }
      ]
    }
    `}
        </script>
      </Helmet>
      <Header />
      <ResponsiveHeroBackground>
        <div className="hero-columns">
          <div className="hero-col hero-col-left">
            <h1>
              Build Confidence with Your Money
              <br />
              Find Peace with Your Plan
            </h1>
            <p className="hero-subtitle">
              Personalized financial coaching designed for your goals, your
              story, your future.
            </p>
            <div className="hero-buttons-container">
              <button
                className="primary-btn"
                onClick={toggleContactModal}
                style={{ fontSize: "1.2rem", padding: "1rem 2.5rem" }}
              >
                Begin Your Consultation Request
              </button>
              <Link
                to="/coachingservices#financial-fitness"
                className="secondary-btn"
                style={{ fontSize: "1.2rem", padding: "1rem 2.5rem" }}
              >
                Get Started With A Financial Fitness Assessment
              </Link>
            </div>
          </div>
          <div className="hero-col hero-col-right"></div>
        </div>
      </ResponsiveHeroBackground>

      <section id="challenges-section" className="section">
        <h2>
          In today’s complex world, we face financial challenges that can feel
          overwhelming...
        </h2>
        <p>
          You’re not alone, and you’re not broken. In today’s fast-paced world,
          financial stress can sneak up on anyone. Whether it’s a passing worry
          or a heavy burden that affects your relationships, mood, and overall
          well-being, these feelings are valid, and more common than you might
          think.
        </p>
        <p>
          Your money story didn’t start with you. The way we feel about money is
          often shaped by family beliefs, social pressures, and the constant
          noise of advertising. But here’s the good news: those patterns aren’t
          permanent. With the right guidance, you can rewrite your financial
          narrative and build a future that feels secure, empowered, and aligned
          with your values.
        </p>
        <h2 className="common-challenges">
          Here Are Common Challenges That We’ve All Experienced:
        </h2>
        <div className="four-box-row">
          <div className="custom-box">
            <div className="icon">
              <i className="fas fa-hiking"></i>
            </div>
            <h3> Navigating Financial Hardships</h3>
            <p>
              Life throws curveballs, unexpected expenses, job changes, or debt
              can leave anyone feeling stuck. We help you regain control,
              rebuild confidence, and move forward with clarity and support.
            </p>
          </div>
          <div className="custom-box">
            <div className="icon">
              <i className="fas fa-baby-carriage"></i>
            </div>
            <h3>Buying Your First Home</h3>
            <p>
              You’ve got the dream, now let’s build the plan to make it real.
              Together, we’ll turn your vision of homeownership into a
              confident, achievable goal.
            </p>
          </div>
          <div className="custom-box">
            <div className="icon">
              <i className="fas fa-chart-line"></i>
            </div>
            <h3>Planning for a Secure Future</h3>
            <p>
              From retirement dreams to college funds and everything in between,
              we help you turn vague hopes into concrete plans, so your future
              feels less uncertain and more achievable.
            </p>
          </div>
          <div className="custom-box">
            <div className="icon">
              <i className="fas fa-hands-helping"></i>
            </div>
            <h3>Mastering Your Budget & Habits</h3>
            <p>
              {" "}
              Budgeting doesn’t have to feel restrictive. We help you build
              sustainable financial habits and a spending plan that supports
              your values, reduces stress, and creates lasting change.
            </p>
          </div>
        </div>
        {/* Button at the bottom of the section */}
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <Link to="/services" className="primary-btn">
            View Services Offered
          </Link>
        </div>
      </section>

      <section className="bio-hero-section">
        <div className="bio-hero-row">
          {/* Right Column (Image) - Moved to be FIRST */}
          <div className="bio-hero-col bio-hero-col-right">
            <div className="bio-image-frame">
              <ResponsiveHeadshot
                headshotNumber={1}
                alt="Wendy Toh"
                className="bio-headshot"
              />
              <div className="certification-badge-container">
                <div className="credentials-container">
                  <div className="seal-column">
                    <OptimizedImage
                      src="/Seal.png"
                      alt="Certification Seal"
                      className="certification-seal"
                      width="120"
                      height="78"
                      sizes="120px"
                    />
                  </div>
                  <div className="name-column">
                    <h3 className="bio-title" style={{ marginTop: "2rem" }}>
                      Wendy Toh
                      <br />
                      <span style={{ fontSize: "1.1rem", fontWeight: 550 }}>
                        Financial Wellness Coach
                      </span>
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Left Column (Text) - Moved to be SECOND */}
          <div className="bio-hero-col bio-hero-col-left">
            <h2 className="bio-title">
              Let Me Guide You Toward Financial Confidence and Peace of Mind
            </h2>
            <div className="bio-title-sep"></div>
            <div className="bio-text">
              <p>
                I spent more than 30 years as a leader in information technology
                with IBM, where one of my greatest joys was coaching my team to
                grow, excel, and succeed. Over the years, my own life journey
                has included marriage, divorce, remarriage, and becoming a
                stepmother to three wonderful daughters. With each transition
                came new financial challenges, and I learned firsthand how to
                adapt and make smart financial decisions along the way.
              </p>
              <p>
                Through personal experience, I’ve navigated budgets, savings,
                credit cards, investing, real estate, working with financial
                advisors and accountants, managing taxes, combining finances,
                and negotiating money matters. Much of this I learned the hard
                way, by researching, experimenting, and sometimes making
                mistakes, but each step helped me build a strong foundation for
                financial wellness. Now retired from corporate America, I am
                living life with intention and purpose. I view this coaching
                practice as a way to share what I’ve learned and to support
                others in creating the life they want. My passion is helping you
                move toward greater financial confidence, balance, and peace of
                mind so you can live with the security and freedom you deserve.
              </p>
            </div>

            <div className="bio-title-sep"></div>
          </div>
        </div>
      </section>

      <section className="two-row-three-col-section">
        <h2
          className="package-grid"
          style={{ textAlign: "center", marginBottom: "2rem" }}
        >
          <span className="title-underline">1-on-1 Coaching Packages</span>
        </h2>
        <div className="trtc-row">
          {/* Row 1 - Item 1 (Dream to Doorstep) - This will be the top-left item */}
          <div className="trtc-pair">
            <Link
              to="/coachingservices#dream-to-doorstep"
              className="trtc-link"
            >
              <div className="trtc-icon-title-text">
                <OptimizedImage
                  src="/D2DIcon.png"
                  alt="Paper cut out of house with coins stacked around it"
                  className="trtc-icon-img"
                  width="84"
                  height="84"
                  sizes="84px"
                />
                <h3 className="trtc-title">Dream to Doorstep:</h3>
                <p>
                  Transform your vision of homeownership into a clear,
                  achievable financial plan.
                </p>
              </div>
            </Link>
          </div>

          {/* Row 1 - Item 2 */}
          <div className="trtc-pair">
            <OptimizedImage
              src="/getbackonyourfeet.png"
              alt="Organization"
              className="trtc-img"
              width="402"
              height="285"
              sizes="(max-width: 768px) 100vw, 402px"
            />
          </div>

          <div className="trtc-pair">
            <Link to="/coachingservices#know-your-money" className="trtc-link">
              <div className="trtc-icon-title-text">
                <OptimizedImage
                  src="/KnowYourMoneyIcon.png"
                  alt="Hands holding web"
                  className="trtc-icon-img"
                  width="84"
                  height="84"
                  sizes="84px"
                />
                <h3 className="trtc-title">Know Your Money:</h3>
                <p>
                  If you’ve ever felt unsure about where your money’s going,
                  let's make sense of it together.
                </p>
              </div>
            </Link>
          </div>
          <div className="trtc-pair">
            <OptimizedImage
              src="/moneymoon.png"
              alt="Small wooden house lined up with paper cutout of family with stacks of coins and a burlap bag stuffed with money"
              className="trtc-img"
              width="402"
              height="285"
              sizes="(max-width: 768px) 100vw, 402px"
            />
          </div>

          <div className="trtc-pair">
            <OptimizedImage
              src="/D2DImg.png"
              alt="New Package D Image"
              className="trtc-img"
              width="402"
              height="285"
              sizes="(max-width: 768px) 100vw, 402px"
            />
          </div>
          <div className="trtc-pair">
            <Link to="/coachingservices#getOnYourFeet" className="trtc-link">
              <div className="trtc-icon-title-text">
                <OptimizedImage
                  src="/GetOnYourFeetIcon.png"
                  alt="Two hands stacked holding a heart"
                  className="trtc-icon-img"
                  width="84"
                  height="84"
                  sizes="84px"
                />
                <h3 className="trtc-title">Get On Your Feet:</h3>
                <p>
                  When life changes suddenly, find stability after life shifts.
                </p>
              </div>
            </Link>
          </div>
          <div className="trtc-pair">
            <OptimizedImage
              src="/knowyourmoney.png"
              alt="money growing out of money"
              className="trtc-img"
              width="402"
              height="285"
              sizes="(max-width: 768px) 100vw, 402px"
            />
          </div>
          <div className="trtc-pair">
            <Link to="/coachingservices#moneymoon" className="trtc-link">
              <div className="trtc-icon-title-text">
                <OptimizedImage
                  src="/MoneyMoonIcon.png"
                  alt="Hands together"
                  className="trtc-icon-img"
                  width="84"
                  height="84"
                  sizes="84px"
                />
                <h3 className="trtc-title">MoneyMoon:</h3>
                <p>
                  For couples who want to get on the same page when talking
                  about money with your partner isn’t always easy.
                </p>
              </div>
            </Link>
          </div>
        </div>
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <Link to="/coachingservices" className="primary-btn">
            View Services Offered
          </Link>
        </div>
      </section>

      <section id="resources" className="section">
        <h2 className="contact-us">
          {" "}
          Schedule a Complimentary Consultation to See If I Can Help You!
        </h2>
        <div className="home-consult-container">
          <button
            className="primary-btn"
            onClick={toggleContactModal}
            style={{ fontSize: "1.2rem", padding: "1rem 2.5rem" }}
          >
            Begin Your Consultation Request
          </button>
        </div>
      </section>

      {isContactModalOpen && <ContactUs onClose={toggleContactModal} />}

      <Footer />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<AboutPage />} />
        <Route
          path="/communityresources"
          element={<CommunityResourcesPage />}
        />

        <Route path="/coachingservices" element={<Services />} />
        <Route
          path="/workshops-seminars"
          element={<WorkshopsAndSeminarsPage />}
        />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacypolicy" element={<PrivacyPolicy />} />
        <Route path="/websitedisclaimer" element={<WebsiteDisclaimer />} />
      </Routes>
    </Router>
  );
}
